/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { Card, Form, Input, Space, Typography } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CreateAdminOauthClientResponse } from '@/client';
import { ContentContainer } from '@/components/ContentContainer';
import { PageHeader } from '@/components/PageHeader';
import { useMessage } from '@/hooks/message';
import { useCreateM2MClientMutation } from '@/hooks/mutation/useCreateM2MClientMutation';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { PermissionsSection } from '@/pages/APITokens/PermissionsSection';
import { useM2MAvailablePermissions } from '@/pages/M2MClients/hooks/useM2MAvailablePermissions';
import { M2MClientCreatedModal } from '@/pages/M2MClients/M2MClientCreatedModal';
import { Button } from '@/primitives/Button';
import { Container } from '@/primitives/Container';
import { Text } from '@/primitives/Text';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

const { Title } = Typography;

type PermissionState = {
  [permissionName: string]: boolean;
};

type FormValues = {
  name: string;
};

export function CreateM2MClientPage() {
  const { t } = useMimicTranslation('m2mClients');
  const [form] = Form.useForm<FormValues>();
  const navigate = useNavigate();
  const [message] = useMessage();
  const tenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const [createdClient, setCreatedClient] = useState<CreateAdminOauthClientResponse | undefined>();
  const [permissionState, setPermissionState] = useState<PermissionState>({});

  const { data: permissionGroups, isLoading: permissionsLoading } = useM2MAvailablePermissions();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {
      message.error(t('failedCopy'));
    });
  };

  const { mutate: createM2MClient, isPending } = useCreateM2MClientMutation({
    onError: (error) => {
      message.error(t('creationError'));
      if (error instanceof Error) {
        form.setFields([
          {
            name: 'name',
            errors: [error.message],
          },
        ]);
      }
    },
    onSuccess: (client) => {
      message.success(t('clientCreated'));
      setCreatedClient(client);
    },
  });

  const selectedScopes = Object.entries(permissionState)
    .filter(([, isEnabled]) => isEnabled)
    .map(([permission]) => permission);

  const onFinish = (values: FormValues) => {
    if (selectedScopes.length === 0) {
      message.error(t('form.scopesRequired'));
      return;
    }

    createM2MClient({
      name: values.name.trim(),
      allowedScopes: selectedScopes,
    });
  };

  const handlePermissionToggle = (permissionName: string, enabled: boolean) => {
    setPermissionState((prev) => ({
      ...prev,
      [permissionName]: enabled,
    }));
  };

  const handleModalClose = () => {
    setCreatedClient(undefined);
    navigate(`/tenants/${tenantID}/m2m-clients`);
  };

  if (!tenantID) return null;

  return (
    <ContentContainer width="100%">
      <Container gap="lg" style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
        <Container gap="sm">
          <PageHeader title={t('createClient.title')} level={2} />
          <Text type="subtle" style={{ fontSize: '15px', lineHeight: '1.4' }}>
            {t('description')}
          </Text>
        </Container>

        <Form form={form} layout="vertical" onFinish={onFinish} style={{ width: '100%' }} disabled={isPending}>
          <Space direction="vertical" size={24} style={{ width: '100%' }}>
            <Card>
              <Title level={4} style={{ margin: 0 }}>
                {t('form.generalInfo')}
              </Title>
              <Form.Item
                name="name"
                label={t('form.nameLabel')}
                rules={[
                  { required: true, message: t('form.nameRequired') },
                  { max: 70, message: t('form.nameMaxLength') },
                ]}
                help={t('form.nameHelp')}
              >
                <Input placeholder={t('form.namePlaceholder')} />
              </Form.Item>
            </Card>

            <PermissionsSection
              permissionGroups={permissionGroups}
              permissionsLoading={permissionsLoading}
              permissionState={permissionState}
              onPermissionToggle={handlePermissionToggle}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px' }}>
              <Button type="default" onClick={() => navigate(`/tenants/${tenantID}/m2m-clients`)}>
                {t('form.cancel')}
              </Button>
              <Button type="primary" htmlType="submit" loading={isPending} disabled={selectedScopes.length === 0}>
                {t('form.create')}
              </Button>
            </div>
          </Space>
        </Form>
      </Container>
      {createdClient && (
        <M2MClientCreatedModal
          createdClient={createdClient}
          onClose={handleModalClose}
          copyToClipboard={copyToClipboard}
        />
      )}
    </ContentContainer>
  );
}
